import { useEffect, useMemo, useState } from 'react';

import {
  ConversionCostsByCountryInfo,
  getCostInfoForCountry,
} from './services';
import {
  EmployeeIncomeInfo,
  EmployerExpensesInfo,
  SalaryFormatSwitch,
  SalaryInput,
  NoDataForCountryPlaceholder,
  BreakdownChart,
} from 'components';
import { getParentAppLocationObject } from 'iframe';
import { countriesWithCodesAndStates } from 'consts';

import type { Country } from 'types';

function App() {
  const [isEmployeeMode, setIsEmployeeMode] = useState(false);
  const [countryFromUrl, setCountryFromUrl] = useState<Country | null>(null);
  const [timeFrameMode, setTimeFrameMode] = useState<'monthly' | 'annual'>(
    'annual'
  );
  const [annualGrossSalary, setAnnualGrossSalary] = useState<string>('100000');
  const [costInfo, setCostInfo] = useState<ConversionCostsByCountryInfo | null>(
    null
  );
  const [error, setError] = useState(null);

  const getCostData = async (
    matchedCountryObject: Country | null,
    annualGrossSalaryAmount: string | number
  ) => {
    const numberAnnualGrossSalary = Number(annualGrossSalaryAmount);
    if (!numberAnnualGrossSalary || !matchedCountryObject) return;

    const { error, data } = await getCostInfoForCountry({
      annualGrossSalary: numberAnnualGrossSalary,
      countryCode: matchedCountryObject.countryCode,
      stateOrProvince: matchedCountryObject?.stateOrProvince,
    });

    if (error) {
      setError(error);
      return error;
    }

    setCostInfo(data);
    return data;
  };

  const getCountryFromAppLocation = async () => {
    const locationObject = await getParentAppLocationObject();

    const matchedCountryObject =
      countriesWithCodesAndStates.find(({ urlSlug }) =>
        locationObject.pathname.toLowerCase().includes(urlSlug.toLowerCase())
      ) ?? null;

    setCountryFromUrl(matchedCountryObject);

    return matchedCountryObject;
  };

  const getInitData = async () => {
    const matchedCountryObject = await getCountryFromAppLocation();
    getCostData(matchedCountryObject, annualGrossSalary);
  };

  useEffect(() => {
    getInitData();

    const searchParams = new URLSearchParams(location.search);
    setIsEmployeeMode(searchParams.has('employeeversion'));
  }, []);

  const convertCosts = (annualValue: number, cutAfterComa = 2) => {
    if (timeFrameMode === 'annual')
      return Number(annualValue.toFixed(cutAfterComa));

    return Number((annualValue / 12).toFixed(cutAfterComa));
  };

  const employerPays = useMemo(() => {
    if (!costInfo) {
      return {
        gross: convertCosts(0),
        taxes: convertCosts(0),
        total: convertCosts(0),
        taxesBreakdown: [],
      };
    }

    const {
      annualGrossSalary: gross,
      taxes: {
        employer: { total: taxes, contributions },
      },
    } = costInfo;

    return {
      gross: convertCosts(gross),
      taxes: convertCosts(taxes),
      total: convertCosts(taxes + gross),
      taxesBreakdown: contributions.map((contribution) => ({
        ...contribution,
        amount: convertCosts(contribution.amount),
      })),
    };
  }, [costInfo, timeFrameMode]);

  const employeeGets = useMemo(() => {
    if (!costInfo) {
      return {
        gross: convertCosts(0),
        taxes: convertCosts(0),
        total: convertCosts(0),
        taxesBreakdown: [],
      };
    }

    const {
      annualGrossSalary: gross,
      taxes: {
        employee: { total: taxes, contributions },
      },
    } = costInfo;

    return {
      gross: convertCosts(gross),
      taxes: convertCosts(taxes),
      total: convertCosts(gross - taxes),
      taxesBreakdown: contributions.map((contribution) => ({
        ...contribution,
        amount: convertCosts(contribution.amount),
      })),
    };
  }, [costInfo, timeFrameMode]);

  const mainBlockRenderer = () => {
    if (error) return <NoDataForCountryPlaceholder />;

    if (costInfo) {
      return (
        <>
          {!isEmployeeMode && (
            <BreakdownChart
              employerPays={employerPays}
              employeeGets={employeeGets}
              onGrossSalaryChange={async (amount) => {
                const roundedNumberAmount = Math.round(Number(amount));
                const castedToAnnual =
                  timeFrameMode === 'monthly'
                    ? roundedNumberAmount * 12
                    : roundedNumberAmount;

                await getCostData(countryFromUrl, castedToAnnual);
                setAnnualGrossSalary(String(castedToAnnual.toFixed(0)));
              }}
            />
          )}
          <div className="flex justify-between mobile:flex-col mb-8">
            {!isEmployeeMode && (
              <>
                <EmployerExpensesInfo
                  employerPays={employerPays}
                  timeFrameMode={timeFrameMode}
                />
                <div className="w-5 mobile:h-5" />
              </>
            )}
            <EmployeeIncomeInfo
              employeeGets={employeeGets}
              timeFrameMode={timeFrameMode}
            />
          </div>
        </>
      );
    }

    // return <GetStartedPlaceholder />;
  };

  return (
    <div className="m-auto py-12 px-10 mobile:py-6 mobile:px-5 text-[#171717]">
      <div className="w-full mb-8">
        <SalaryInput
          setAnnualGrossSalary={setAnnualGrossSalary}
          onSubmit={() => getCostData(countryFromUrl, annualGrossSalary)}
          annualGrossSalary={annualGrossSalary}
        />
        <SalaryFormatSwitch
          setTimeFrameMode={setTimeFrameMode}
          timeFrameMode={timeFrameMode}
        />
      </div>

      <div className="min-h-[500px]">{mainBlockRenderer()}</div>
    </div>
  );
}

export default App;
